import { Box, Button, ButtonGroup } from '@mui/material'
import { useState } from 'react'

import { Loader } from '@/components'
import { MonacoEditorField } from '@/components/MonacoEditor'
import { useAppContext } from '@/hooks'
import { SourceItem } from '@/rtkApi/mddApiSliceGeneratedV2'

import { DescriptionFieldWithFullscreen } from '../../components/DescriptionFieldWithFullscreen'
import { SourceTableV2 } from '../../components/SourceTableV2'
import { useViewDataContext } from '../../ViewDataContext'

export const SourcesV2 = () => {
	const { t } = useAppContext()
	const { form, sourceItems, editMode, onChange, onSave, isLoading: isSourcesLoading } =
		useViewDataContext()

	const [isStructuredView, setIsStructuredView] = useState(
		(sourceItems?.length ?? 0) > 0 || !form?.sourceScript
	)

	if (isSourcesLoading || !form) {
		return <Loader loaded={false} />
	}

	const isReadOnly = !editMode

	const handleSourceItemsChange = (value: { sourceItems: SourceItem[] }) => {
		onChange(value)
	}

	const viewToggle = (
		<ButtonGroup size="small">
			<Button
				variant={isStructuredView ? 'contained' : 'outlined'}
				onClick={() => setIsStructuredView(true)}
			>
				{t('STRUCTURED_SOURCES')}
			</Button>
			<Button
				variant={!isStructuredView ? 'contained' : 'outlined'}
				onClick={() => setIsStructuredView(false)}
			>
				{t('TEXT_SOURCES')}
			</Button>
		</ButtonGroup>
	)

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'auto' }}>
			{/* Main content: no flex:1 so fields keep natural height; single scroll for page, no per-field scrollbar or label clipping */}
			<Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0 }}>
				{/* Toggle buttons - always outside the table/editor */}
				<Box sx={{ display: 'flex', alignItems: 'center' }}>
					{viewToggle}
				</Box>

				{isStructuredView ? (
					/* STRUCTURED MODE - Table */
					<SourceTableV2
						sourceItems={sourceItems ?? []}
						onChange={handleSourceItemsChange}
						onSave={onSave}
					/>
				) : (
					/* TEXT MODE - Monaco: 2 rows when empty, 10 rows when filled */
					<MonacoEditorField
						title={t('VIEW_SOURCE')}
						value={form.sourceScript ?? ''}
						onChange={(value) => onChange({ sourceScript: value })}
						disabled={isReadOnly}
						language="sql"
						isSqlScript
					/>
				)}


				{/* Source Description - same look as Criteria script + fullscreen + resize */}
				<DescriptionFieldWithFullscreen
					title={t('VIEW_SOURCE_SCRIPT_DESCRIPTION')}
					value={form.sourceDescription ?? ''}
					onChange={(v) => onChange({ sourceDescription: v })}
					readOnly={isReadOnly}
					helperText={t('MARKDOWN_SUPPORTED')}
				/>

				{/* Criteria Script - Monaco: 2 rows when empty, 10 rows when filled */}
				<MonacoEditorField
					title={t('VIEW_CRITERIA')}
					value={form.criteriaScript ?? ''}
					onChange={(value) => onChange({ criteriaScript: value })}
					disabled={isReadOnly}
					language="sql"
					isSqlScript
				/>

				{/* Criteria Description - same look as Criteria script + fullscreen + resize */}
				<DescriptionFieldWithFullscreen
					title={t('VIEW_CRITERIA_SCRIPT_DESCRIPTION')}
					value={form.criteriaDescription ?? ''}
					onChange={(v) => onChange({ criteriaDescription: v })}
					readOnly={isReadOnly}
					helperText={t('MARKDOWN_SUPPORTED')}
				/>
			</Box>
		</Box>
	)
}
